/**
 * Free gift that puts itself in the cart.
 *
 * The money side is not ours: an automatic discount in the admin zeroes the gift
 * when a qualifying product is present. This only saves the shopper from having to
 * add it by hand — and, just as importantly, takes it back out when the qualifying
 * product leaves, because a gift left behind stops being discounted and quietly
 * turns into a 99 shekel line nobody chose.
 *
 * The gift line carries a hidden `_gift` property. That is what tells it apart from
 * the same product bought deliberately: without it we would remove an item the
 * shopper paid for. Properties starting with an underscore are hidden at checkout,
 * so the line reads as an ordinary product at 0.
 *
 * Cart mutations are caught by wrapping fetch rather than by listening to theme
 * events: the theme has several components that touch the cart, and every one of
 * them goes through fetch. Our own requests use the saved original, so they never
 * re-enter. After a change lands the theme is asked to redraw itself through its
 * own event bus — we render nothing.
 *
 * Config comes from snippets/gift-auto-add.liquid (theme settings).
 */
(() => {
  const config = window.themeGiftConfig;
  if (!config?.enabled || !config.variantId || !config.qualifyingIds?.length) return;

  const GIFT_FLAG = '_gift';
  const qualifying = new Set(config.qualifyingIds);
  const root = window.Shopify?.routes?.root ?? '/';
  const nativeFetch = window.fetch.bind(window);

  let working = false;

  const isCartMutation = (url) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);

  const isGiftProduct = (item) => (item.variant_id ?? item.id) === config.variantId;

  const postJson = (path, body) =>
    nativeFetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

  const readCart = async () => {
    const response = await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
    return response.json();
  };

  /** Ask the theme to redraw. On the cart page there is no drawer to redraw and the
   *  markup was rendered before our change, so the page has to come back fresh. */
  const refresh = async () => {
    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }

    window.eventBus?.emit('render:cart:drawer');

    try {
      window.wetheme?.updateCartCount?.(await readCart());
    } catch {
      /* The count is cosmetic; a failure here must not surface to the shopper. */
    }
  };

  const reconcile = async () => {
    if (working) return;
    working = true;

    try {
      const cart = await readCart();
      const gift = cart.items.find((item) => item.properties?.[GIFT_FLAG]);
      const qualifies = cart.items.some(
        (item) => !item.properties?.[GIFT_FLAG] && qualifying.has(item.product_id)
      );

      let changed = false;

      // Never a second line of the same product. A shopper who had already put the
      // serum in the cart used to end up with two: the discount frees exactly one,
      // and Shopify picks which — it picked theirs, leaving the line we had labelled
      // "gift" as the one being charged. One line cannot be mislabelled.
      if (qualifies && !gift && !cart.items.some(isGiftProduct)) {
        // Out of stock is a real possibility and not an error worth showing: the
        // shopper still gets the product they came for.
        const added = await postJson('cart/add.js', {
          items: [{ id: config.variantId, quantity: 1, properties: { [GIFT_FLAG]: 'true' } }]
        });
        changed = added.ok;
      } else if (!qualifies && gift) {
        await postJson('cart/change.js', { id: gift.key, quantity: 0 });
        changed = true;
      } else if (gift && gift.quantity > 1) {
        // The discount frees exactly one. A second copy would be charged in full.
        await postJson('cart/change.js', { id: gift.key, quantity: 1 });
        changed = true;
      }

      if (changed) await refresh();
    } catch (error) {
      console.error('Gift auto-add:', error);
    } finally {
      working = false;
    }
  };

  let observer = null;

  /** Label the free line, and take its controls away when it is ours.
   *
   *  Locking matters: without it the shopper sets the gift to zero, we put it
   *  straight back, and the cart visibly argues with them. A free gift that cannot
   *  be declined is ordinary; a gift that bounces is not.
   *
   *  The line is found by position — the theme gives it no id of its own, and
   *  underscore properties are not rendered at all, so there is nothing else to
   *  match on. */
  const markGiftLine = async () => {
    let index = -1;
    let locked = false;

    try {
      const cart = await readCart();
      // Label whichever line the discount actually landed on, not the one we added.
      // Shopify decides that, and a badge on a line still being charged is a lie.
      index = cart.items.findIndex((item) => isGiftProduct(item) && item.total_discount > 0);
      if (index < 0) index = cart.items.findIndex((item) => item.properties?.[GIFT_FLAG]);
      // Controls come off only on a line we own. A line the shopper is paying for
      // has to stay removable, whatever it says on it.
      locked = index >= 0 && Boolean(cart.items[index].properties?.[GIFT_FLAG]);
    } catch {
      return; /* Cosmetic. A cart that works without the badge beats an error. */
    }

    // Our own edits land inside the watched subtree, so the observer has to be off
    // while we make them or it retriggers itself forever.
    observer?.disconnect();

    for (const marked of document.querySelectorAll('[data-gift-line]')) {
      marked.removeAttribute('data-gift-line');
      marked.removeAttribute('data-gift-locked');
      marked.querySelector('.cart-gift-badge')?.remove();
    }

    for (const found of index < 0 ? [] : findLines(index)) {
      found.line.setAttribute('data-gift-line', '');
      if (locked) found.line.setAttribute('data-gift-locked', '');
      const badge = document.createElement('span');
      badge.className = 'cart-gift-badge';
      badge.textContent = config.label;
      found.anchor.after(badge);
    }

    observe();
  };

  /** Every place the cart is drawn, not the first one found: on /cart the drawer
   *  sits in the same document as the table, so marking one of them left the other
   *  showing the gift as an ordinary 99 shekel line.
   *
   *  Neither markup gives a line an id of its own, and underscore properties are
   *  not rendered at all, so position is what there is: the drawer numbers its
   *  quantity controls, the page just lists rows. Both follow cart order. */
  const findLines = (index) => {
    const found = [];

    const control = document.querySelector(`cart-quantity[data-item-index="${index + 1}"]`);
    if (control) {
      found.push({
        line: control.closest('.cart-drawer__product_card') ?? control.parentElement,
        anchor: control
      });
    }

    const row = document.querySelectorAll('tr.cart__row')[index];
    const anchor = row?.querySelector('.js-qty-wrapper') ?? row?.querySelector('.js-qty');
    if (row && anchor) found.push({ line: row, anchor });

    return found;
  };

  const containers = () =>
    [
      document.querySelector('[data-cart-dynamic-content="cart-body"]'),
      document.querySelector('table.cart-table')
    ].filter(Boolean);

  // The drawer replaces its own body wholesale on every render, taking our mark
  // with it. Watching is more dependable than guessing when each of the theme's
  // cart components has finished drawing.
  const observe = () => {
    for (const target of containers()) observer.observe(target, { childList: true, subtree: true });
  };

  const watch = () => {
    if (!containers().length) return;
    observer = new MutationObserver(() => markGiftLine());
    observe();
    markGiftLine();
  };

  /** Reconciling on load is not belt and braces, it is the only cover for the ways
   *  a cart changes without a fetch. The cart page removes a line through a plain
   *  link (`/cart/change?line=…`), the browser navigates, and no wrapper of ours
   *  ever runs — the gift stayed behind, no longer discounted, priced at full. The
   *  same goes for a shopper returning days later to a cart left in the cookie. */
  const start = () => {
    watch();
    reconcile();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);

    const target = args[0];
    const url = typeof target === 'string' ? target : (target?.url ?? '');

    // Not awaited on purpose: the theme is waiting on this response to draw the
    // drawer, and holding it back would show a stalled cart.
    if (!working && response.ok && isCartMutation(String(url))) reconcile();

    return response;
  };
})();
